import React, { useEffect, useState } from "react";
import axios from "axios";

const Category = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // ✅ loading state

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!categoryName) {
      alert("Please enter a category name.");
      return;
    }

    const formData = new FormData();
    formData.append("name", categoryName);
    if (imageFile) {
      formData.append("image", imageFile);
    }

    setIsLoading(true); // ✅ start loading
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/admin/categories`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Category created:", response.data);
      setCategoryName("");
      setImageFile(null);
      setImagePreview(null);
      setIsModalOpen(false);
      fetchCategories(); // refresh
    } catch (error) {
      console.error(
        "Failed to create category:",
        error.response?.data || error.message
      );
      alert("Failed to create category.");
    } finally {
      setIsLoading(false); // ✅ end loading
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE_URL}/api/admin/categories`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCategories(response.data);
    } catch (err) {
      console.error(
        "Failed to fetch categories",
        err.response?.data || err.message
      );
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this category?"
    );
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${import.meta.env.VITE_API_BASE_URL}/api/admin/categories/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchCategories(); // refresh
    } catch (error) {
      console.error("Delete failed:", error.response?.data || error.message);
      alert("Failed to delete the category.");
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return (
    <div className="w-full flex flex-col gap-8">
      <div className="w-full flex flex-col gap-4 bg-[#00294B] rounded-md">
        <h2 className="px-4 pt-4 text-2xl font-bold text-white">Category</h2>
        <div className="w-full flex items-center justify-end gap-4 border-b border-[#22BEFF33] px-4">
          <button
            className="py-1 px-4 bg-[#22BEFF] text-black rounded cursor-pointer font-semibold"
            onClick={() => setIsModalOpen(true)}
          >
            Create
          </button>
        </div>

        <div className="w-full flex flex-col gap-4 p-4 min-h-[20rem]">
          {categories.length === 0 ? (
            <h4 className="text-xl font-semibold text-white text-center">
              No categories found.
            </h4>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-[#022C4E] text-white rounded-md overflow-hidden">
                <thead>
                  <tr className="bg-[#00406B] text-left">
                    <th className="py-3 px-4">ID</th>
                    <th className="py-3 px-4">Image</th>
                    <th className="py-3 px-4">Name</th>
                    <th className="py-3 px-4">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((cat) => (
                    <tr key={cat.id} className="border-b border-[#1a3c5a]">
                      <td className="py-3 px-4">{cat.id}</td>
                      <td className="py-3 px-4">
                        {cat.image ? (
                          <img
                            src={`${import.meta.env.VITE_API_BASE_URL}/storage/${cat.image}`}
                            alt={cat.name}
                            className="w-16 h-16 object-cover rounded"
                          />
                        ) : (
                          <span className="text-gray-400">No Image</span>
                        )}
                      </td>
                      <td className="py-3 px-4">{cat.name}</td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleDelete(cat.id)}
                          className="bg-red-600 hover:bg-red-700 text-white py-1 px-3 rounded"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-md p-6 w-[90%] max-w-md relative">
            <h3 className="text-xl font-semibold mb-4">Create Category</h3>

            {/* Category Name */}
            <input
              type="text"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="Category name"
              className="w-full border border-gray-300 p-2 rounded mb-4"
              disabled={isLoading}
            />

            {/* Image Upload */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                disabled={isLoading}
                className="block w-full text-sm text-gray-500 file:py-2 file:px-4 file:border-0 file:bg-[#22BEFF] file:text-white file:rounded cursor-pointer"
              />
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="mt-4 w-full h-auto rounded border"
                />
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-gray-300 rounded"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className={`px-4 py-2 rounded text-white ${
                  isLoading
                    ? "bg-blue-300 cursor-not-allowed"
                    : "bg-[#22BEFF] hover:bg-[#1ba6e0]"
                }`}
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Save"}
              </button>
            </div>

            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-2 right-2 text-black hover:text-red-500 text-xl"
              disabled={isLoading}
            >
              &times;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Category;
